import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { Logger } from '../../logging/logger.service';

const CORRELATION_HEADER = 'X-Correlation-Id';

const generateCorrelationId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const isCrossOriginRequest = (url: string): boolean => {
  if (!/^https?:\/\//i.test(url)) {
    return false;
  }

  try {
    return new URL(url).origin !== window.location.origin;
  } catch {
    return false;
  }
};

export const correlationIdInterceptor: HttpInterceptorFn = (req, next) => {
  const logger = inject(Logger);

  if (isCrossOriginRequest(req.url)) {
    logger.debug('Skipping correlation header for cross-origin request', {
      feature: 'http',
      operation: 'correlation-id',
      severity: 'low',
      url: req.urlWithParams,
    });

    return next(req);
  }

  const correlationId = req.headers.get(CORRELATION_HEADER) ?? generateCorrelationId();

  logger.debug('Applied correlation id interceptor', {
    correlationId,
    feature: 'http',
    operation: 'correlation-id',
    severity: 'low',
  });

  const requestWithCorrelationId = req.clone({
    setHeaders: {
      [CORRELATION_HEADER]: correlationId,
    },
  });

  return next(requestWithCorrelationId);
};
